import { hashKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import { useMyIntegrationEntityIds } from '@/hooks/integrations/useMyIntegrationEntityIds';
import { useCurrentUserEntity } from '@/hooks/useCurrentUserEntity';
import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { usePermissions } from '@/hooks/usePermissions';
import { useRefreshAt } from '@/hooks/useRefreshAt';
import { useReminderAutomationEnabled } from '@/hooks/useReminderAutomationEnabled';
import * as bootMetrics from '@/lib/boot/bootMetrics';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { useStoreBackedList } from '@/lib/crm/store';
import {
  seedEmptyHiddenMeetingsIfUnseeded,
  useHiddenMeetings,
} from '@/lib/dashboard/hiddenMeetings';
import {
  todayMeetingsQueryOptions,
  todayRemindersQueryOptions,
  todayTasksQueryOptions,
} from '@/lib/dashboard/queries';
import {
  buildTodayMeetingsFilters,
  getDashboardDateRange,
  getDashboardReminderDateRange,
} from '@/lib/dates/dashboardDateRange';
import { getPersistedDashboardPrefetch } from '@/lib/localStorage/persistedDashboardPrefetch';
import { isPrefetchDebugEnabled, prefetchDebug } from '@/lib/prefetch/debug';
import type { ProposalListItem } from '@/lib/proposals/core/types';
import { useMyPendingProposalsPaginated } from '@/lib/proposals/hooks/useProposals';
import { proposalQueryKeys } from '@/lib/proposals/query/keys';
import {
  type CalendarEvent,
  mapCrmObjectsToCalendarEvents,
} from '@/pages/calendar/utils/eventMapper';
import { isReminderVisibleToUser } from '@/pages/calendar/utils/reminderUtils';
import {
  isTaskInTerminalStage,
  isTaskVisibleToUser,
} from '@/pages/calendar/utils/taskUtils';
import type { CrmObject } from '@/types/crm';

import { formatCurrency, useDashboardMetrics } from '../useDashboardMetrics';
export type { CalendarEvent } from '@/pages/calendar/utils/eventMapper';

export const LOADING_FALLBACK_NAME = 'there';

/**
 * How long useCalendarEvents waits, after mount, for something else to seed
 * the hidden-meetings cache (a real prefetch result, or a dismiss/undismiss
 * write) before falling open with an empty set itself. Long enough that a
 * normal in-flight quickfetch call finishes first (so its real result wins);
 * short enough that a session where the prefetch script never ran at all
 * (see hiddenMeetings.ts's module doc) doesn't leave the dashboard's live
 * meetings query blocked for long.
 */
export const HIDDEN_MEETINGS_FALLBACK_SEED_DELAY_MS = 4000;

export interface CalendarData {
  events: CalendarEvent[];
  /** Number of events occurring today (used for meetingsToday metric) */
  todayCount: number;
  /** Number of meetings whose start_time is still in the future (used for meetingsUpcoming metric) */
  upcomingCount: number;
  /** Integration entity IDs the meetings filter is built from. Empty when the user has no connected integrations or the integration query errored. */
  integrationEntityIds: string[];
  /** Today's reminder objects (ahoy_start_date in today's date range) */
  reminders: CrmObject[];
  /** Associated objects for today's reminders (keyed by entity_id) */
  remindersAssociatedObjects: Record<string, CrmObject>;
  /** Today's task objects (ahoy_datetime due today) */
  tasks: CrmObject[];
  /** Associated objects for today's tasks (keyed by entity_id) */
  tasksAssociatedObjects: Record<string, CrmObject>;
  isLoading: boolean;
  error: string | null;
}

export interface ActionCategoryCounts {
  onboarding: number;
  email: number;
  pipeline_update: number;
  qualified_leads: number; // Maps to crm_create
  total: number;
}

export interface DashboardActions {
  items: ProposalListItem[];
  counts: ActionCategoryCounts;
  isLoading: boolean;
  /** Whether recipient data (company badges) is still loading */
  isLoadingRecipientData: boolean;
  error: string | null;
}

export interface DashboardMetricsData {
  totalPipelineValue: number;
  weightedPipelineValue: number;
  dealsCount: number;
  activeDealCount: number;
  contactsCount: number;
  companiesCount: number;
  meetingsToday: number;
  /** Number of meetings whose start_time is still in the future */
  meetingsUpcoming: number;
  aiActionsPrepared: number;
  currencyCode: string;
  pipelineStages: Array<{
    stageEntityId: string;
    stageLabel: string;
    dealCount: number;
    totalValue: number;
    /** Configured stage color (ahoy_color, hex/CSS). Absent = no color set. */
    stageColor?: string;
  }>;
  isLoading: boolean;
}

export interface DashboardData {
  calendar: CalendarData;
  actions: DashboardActions;
  metrics: DashboardMetricsData;
  greeting: string;
  firstName: string;
}

// ============================================================================
// Calendar Hook
// ============================================================================

/**
 * The next local midnight. Used so date-scoped dashboard content (today's
 * meeting counts, the "X more meetings" indicator, the tasks "due today"
 * range) refreshes when the calendar day rolls over. `setHours(24, ...)`
 * lands on the actual wall-clock midnight, so it stays correct across DST
 * transitions (where a day is 23 or 25 hours).
 */
function getNextLocalMidnight(): number {
  const midnight = new Date();
  midnight.setHours(24, 0, 0, 0);
  return midnight.getTime();
}

/**
 * Hook to fetch today's calendar events for the current user.
 * Returns only the current user's non-cancelled meetings.
 * `isLoading` is true while either the CRM query or the user entity lookup is in progress.
 * @param orgId - Organization ID
 * @param enabled - Whether to fetch at all (caller can gate fetching)
 */
export function useCalendarEvents(orgId: number | undefined, enabled = true) {
  const displayTimeZone = useDisplayTimezone();
  // Calculate today-only date range in local timezone
  const { rangeStartISO, rangeEndISO } = getDashboardDateRange(displayTimeZone);

  // Get current user's integration entity IDs to filter meetings by integration association
  const {
    integrationEntityIds,
    isLoading: isIntegrationsLoading,
    isLoaded: isIntegrationsLoaded,
  } = useMyIntegrationEntityIds(orgId);

  const { entityId, isLoading: isUserEntityLoading } = useCurrentUserEntity();

  // Per-user hidden-meeting set (dismissed meetings/series). Cache-only —
  // seeded by the dashboard prefetch and by useDismissMeeting; see
  // lib/dashboard/hiddenMeetings.ts.
  const { hiddenMeetings, isLoaded: isHiddenMeetingsLoaded } =
    useHiddenMeetings(orgId);

  // Persisted entity IDs from the previous visit. Synchronous read — the
  // prefetch wrote these in the same visit's post-await block. On revisits
  // the value is available BEFORE OrgContext resolves, so the hook can
  // construct the meetings filter key without waiting on the integrations
  // chain. Wrapped in useMemo so the reference is stable per orgId.
  const persisted = useMemo(
    () => getPersistedDashboardPrefetch(orgId),
    [orgId]
  );

  // Build filter for today's meetings (non-cancelled, grouped by integration association)
  const filterGroups = useMemo(() => {
    return buildTodayMeetingsFilters(
      rangeStartISO,
      rangeEndISO,
      integrationEntityIds,
      entityId,
      hiddenMeetings.seriesIds,
      hiddenMeetings.meetingEntityIds
    );
  }, [
    rangeStartISO,
    rangeEndISO,
    integrationEntityIds,
    entityId,
    hiddenMeetings,
  ]);

  // Fast-path: use prefetch metadata when live data isn't loaded yet.
  // Once live data loads (isIntegrationsLoaded AND isHiddenMeetingsLoaded), it
  // takes over. buildTodayMeetingsFilters uses a defensive sort so keys are
  // byte-identical between the prefetch and the live path — no duplicate
  // fetches.
  const effectiveFilterGroups = useMemo(() => {
    if (isIntegrationsLoaded && isHiddenMeetingsLoaded) {
      return filterGroups;
    }
    if (persisted) {
      return buildTodayMeetingsFilters(
        rangeStartISO,
        rangeEndISO,
        persisted.integrationEntityIds,
        persisted.userEntityId,
        persisted.hiddenMeetings.seriesIds,
        persisted.hiddenMeetings.meetingEntityIds
      );
    }
    return filterGroups;
  }, [
    isIntegrationsLoaded,
    isHiddenMeetingsLoaded,
    filterGroups,
    persisted,
    rangeStartISO,
    rangeEndISO,
  ]);

  // CRM Meeting objects query — options built via the shared builder so the
  // cache key is byte-identical to the one the dashboard prefetch seeds.
  const meetingsQueryOptions = todayMeetingsQueryOptions(
    orgId ?? 0,
    effectiveFilterGroups
  );
  const qcMeetings = useQueryClient();

  // Fallback for sessions that reach the org dashboard without
  // lib/prefetch.ts ever executing — e.g. a client-side navigation from `/`
  // via HomeRedirect/OrgGuard/FullPageOrgSwitcher, none of which trigger a
  // hard page load. The prefetch script (and its own fail-open seed) only
  // runs once at hard load, so without this effect isHiddenMeetingsLoaded
  // would stay false forever for these sessions, and the meetings query
  // below would stay disabled forever with it. Give a real prefetch — if
  // one IS in flight for this session — a window to land first; the
  // never-clobber guard in seedEmptyHiddenMeetingsIfUnseeded means a
  // genuine result always wins over this fallback if it arrives first, and
  // an unconditional setQueryData in the prefetch entry means a genuine
  // result landing AFTER this fallback still overwrites it.
  useEffect(() => {
    if (!orgId || isHiddenMeetingsLoaded) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      prefetchDebug(
        'hidden meetings fallback: seeding empty set (cache still unseeded)',
        { orgId }
      );
      seedEmptyHiddenMeetingsIfUnseeded(qcMeetings, orgId);
    }, HIDDEN_MEETINGS_FALLBACK_SEED_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [orgId, isHiddenMeetingsLoaded, qcMeetings]);

  if (isPrefetchDebugEnabled()) {
    const meetingsKeyHash = hashKey(meetingsQueryOptions.queryKey);
    const existingMeetings = qcMeetings.getQueryCache().get(meetingsKeyHash);
    prefetchDebug('useCalendarEvents mount', {
      meetingsKey: meetingsQueryOptions.queryKey,
      meetingsKeyHash,
      queryClientId: (qcMeetings as unknown as Record<string, unknown>)
        .__debugId,
      integrationEntityIds,
      isIntegrationsLoading,
      cacheHit: !!existingMeetings,
      existingStatus: existingMeetings?.state.status,
      existingFetchStatus: existingMeetings?.state.fetchStatus,
      existingDataUpdatedAt: existingMeetings?.state.dataUpdatedAt,
      enabled:
        !!orgId &&
        enabled &&
        ((isIntegrationsLoaded && isHiddenMeetingsLoaded) || !!persisted),
    });
    prefetchDebug('useCalendarEvents prefetch fast-path', {
      hasPersisted: !!persisted,
      isIntegrationsLoaded,
    });
  }
  const meetingsQuery = useQuery({
    ...meetingsQueryOptions,
    enabled:
      !!orgId &&
      enabled &&
      ((isIntegrationsLoaded && isHiddenMeetingsLoaded) || !!persisted),
    staleTime: 5 * 60_000, // 5 minutes
    gcTime: 5 * 60_000, // 5 minutes
  });
  const meetings = useStoreBackedList(meetingsQuery);
  const events = useMemo(
    () =>
      mapCrmObjectsToCalendarEvents(
        meetings.results,
        meetings.associatedObjects
      ),
    [meetings.results, meetings.associatedObjects]
  );

  // Compute date-only strings for the reminder query. ahoy_start_date is a
  // date field (YYYY-MM-DD), not datetime — ISO datetime strings from
  // getDashboardDateRange() would fail the comparison. Shared with the prefetch
  // entry so the cache key matches (see getDashboardReminderDateRange).
  const { todayDateOnly, tomorrowDateOnly } = useMemo(
    () => getDashboardReminderDateRange(displayTimeZone),
    [displayTimeZone]
  );

  // Hide reminders when the automation is disabled. Gate fetching on this so we
  // don't make a wasted API call when the results would be discarded anyway.
  const remindersEnabled = useReminderAutomationEnabled(orgId);

  // Effective user entity id for the reminders cache key. Mirrors the meetings
  // effectiveFilterGroups fast-path: until the current user's entity id is
  // confirmed loaded, fall back to the persisted id from the prior visit so the
  // key matches the prefetch's pre-await registration (which also used the
  // persisted id). Without this, a first-render key built from a freshly
  // resolved entityId can differ from the prefetch's pre-await `null` key while
  // the post-await re-registration hasn't landed yet, firing a duplicate live
  // request and dropping the warm-cache benefit.
  const effectiveReminderUserEntityId = useMemo(() => {
    if (!isUserEntityLoading) {
      return entityId;
    }
    // Still loading the current user's entity id. Prefer a live entityId if we
    // already have one: the per-user ahoy_dismissed_by_users filter must key off
    // the real current user, so a known live id always wins over the persisted
    // fallback. Falling back to the persisted id (or null) while entityId is
    // already available would briefly build the wrong filter and let dismissed
    // reminders reappear during an org refresh. Only fall back to the prior
    // visit's persisted id when entityId is genuinely not known yet — that keeps
    // the key aligned with the prefetch's pre-await seed.
    if (entityId !== null) {
      return entityId;
    }
    if (persisted) {
      return persisted.userEntityId;
    }
    return entityId;
  }, [isUserEntityLoading, entityId, persisted]);

  // Parallel query for today's reminders — options built via the shared
  // builder so the cache key is byte-identical to the prefetch's seed.
  // effectiveReminderUserEntityId is passed so the per-user dismissed filter and
  // cache key match the prefetch's seed across the null->known transition.
  // enabled is gated (mirroring meetings' `isIntegrationsLoaded || !!persisted`)
  // so the live query doesn't fire under a premature key before the current-user
  // key is registered.
  const remindersQuery = useQuery({
    ...todayRemindersQueryOptions(
      orgId ?? 0,
      todayDateOnly,
      tomorrowDateOnly,
      effectiveReminderUserEntityId
    ),
    enabled:
      !!orgId &&
      enabled &&
      remindersEnabled &&
      (!isUserEntityLoading || !!persisted),
    staleTime: 5 * 60_000,
    gcTime: 5 * 60_000,
  });
  const remindersList = useStoreBackedList(remindersQuery);

  // Instant-range bounds for the tasks query, memoized so the cache key
  // stays stable rather than recomputing on every render like the
  // unmemoized rangeStartISO/rangeEndISO used for the meetings filter.
  // Unlike the reminder date-range memo above (todayDateOnly/tomorrowDateOnly),
  // this one deliberately reacts to the midnight tick: DoD requires "due
  // today" to mean the current day, so a task list left open overnight must
  // refetch with tomorrow's bounds. This is the intended, once-per-day
  // refetch, not a storm.
  const taskMidnightTick = useRefreshAt(getNextLocalMidnight());
  const { rangeStartISO: taskRangeStartISO, rangeEndISO: taskRangeEndISO } =
    useMemo(
      () => getDashboardDateRange(displayTimeZone),
      // taskMidnightTick isn't read inside the callback — it's a bump-only
      // dependency that forces recomputation exactly when useRefreshAt fires.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [taskMidnightTick, displayTimeZone]
    );

  // Gate the tasks query on read permission for ahoy_task — a user without
  // read access should see no query fire and no tasks rendered.
  const { hasPermission } = usePermissions();
  const canReadTasks = hasPermission('ahoy_task');

  // Parallel query for today's tasks due today, mirroring the reminders query
  // above.
  const tasksQuery = useQuery({
    ...todayTasksQueryOptions(
      orgId ?? 0,
      todayDateOnly,
      tomorrowDateOnly,
      taskRangeStartISO,
      taskRangeEndISO
    ),
    enabled: !!orgId && enabled && canReadTasks,
    staleTime: 5 * 60_000,
    gcTime: 5 * 60_000,
  });
  const tasksList = useStoreBackedList(tasksQuery);

  // Task pipelines, used to filter out tasks whose store-fresh current stage
  // is terminal (see isTaskInTerminalStage) — a task completed anywhere
  // drops off the dashboard without a reload. Gated on the same conditions as
  // the task query above so a disabled calendar or a user without task-read
  // permission issues no task-related request.
  const { pipelines: taskPipelines } = usePipelines(
    enabled && canReadTasks ? orgId : undefined,
    'ahoy_task'
  );

  // Perf trace: first arrival of each list, with row counts (never contents).
  useEffect(() => {
    if (meetingsQuery.isSuccess) {
      bootMetrics.markOnce('data:meetings', {
        count: meetings.results.length,
      });
    }
  }, [meetingsQuery.isSuccess, meetings.results.length]);
  useEffect(() => {
    if (remindersQuery.isSuccess) {
      bootMetrics.markOnce('data:reminders', {
        count: remindersList.results.length,
      });
    }
  }, [remindersQuery.isSuccess, remindersList.results.length]);
  useEffect(() => {
    if (tasksQuery.isSuccess) {
      bootMetrics.markOnce('data:tasks', { count: tasksList.results.length });
    }
  }, [tasksQuery.isSuccess, tasksList.results.length]);

  // Track the orgId we last checked against so an org switch re-fires the
  // cache-check for the new org's key (the boot summary fires at +3s, so a
  // fast switch within that window must reflect the active org).
  const meetingsCheckedOrgIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!orgId) {
      return;
    }
    if (meetingsCheckedOrgIdRef.current === orgId) {
      return;
    }
    meetingsCheckedOrgIdRef.current = orgId;
    const meetingsKey = meetingsQueryOptions.queryKey;
    bootMetrics.cacheCheck('meetings', qcMeetings, meetingsKey);
  }, [orgId, qcMeetings, meetingsQueryOptions.queryKey]);

  // All fetched events are today-only, so todayCount is just the total
  const todayCount = events.length;

  // Count meetings whose start_time is still in the future.
  // Intentionally not memoized — the count must reflect the current time on
  // every render so the greeting updates as meetings start.
  const now = new Date();
  const upcomingCount = events.filter(e => new Date(e.start_time) > now).length;

  return useMemo(
    () => ({
      events,
      todayCount,
      upcomingCount,
      integrationEntityIds: isIntegrationsLoaded
        ? integrationEntityIds
        : (persisted?.integrationEntityIds ?? integrationEntityIds),
      // Show only reminders owned by the current user or unowned; hide those
      // owned by someone else (declutter — see isReminderVisibleToUser).
      reminders: remindersEnabled
        ? remindersList.results.filter(r =>
            isReminderVisibleToUser(r, entityId)
          )
        : [],
      remindersAssociatedObjects: remindersEnabled
        ? remindersList.associatedObjects
        : {},
      // Show only tasks owned by the current user or unowned; hide those
      // owned by someone else (declutter — see isTaskVisibleToUser). Also
      // filter out tasks whose store-fresh current stage is terminal, so a
      // task completed anywhere (record view, another tab, a WebSocket
      // event) drops off without a reload — see isTaskInTerminalStage. Also
      // gated on read permission — canReadTasks=false disables the query
      // above so this just renders nothing without an error. Gated on
      // `enabled` too: when the caller disables the hook, the pipelines
      // query above is also disabled, so its data can go away while cached
      // tasks remain — returning them then would pass terminal tasks through
      // isTaskInTerminalStage on an empty pipeline list and un-hide them.
      tasks:
        enabled && canReadTasks
          ? tasksList.results
              .filter(t => isTaskVisibleToUser(t, entityId))
              .filter(t => !isTaskInTerminalStage(t, taskPipelines))
          : [],
      tasksAssociatedObjects:
        enabled && canReadTasks ? tasksList.associatedObjects : {},
      // Show loading until integrations are *confirmed* (loaded, errored,
      // or entity-absent), not merely "not actively fetching". A disabled
      // React Query (entityId null) has isLoading=false but isLoaded=false
      // too — using isIntegrationsLoading here would report "done" with
      // empty integrationEntityIds, flashing "Connect your calendar"
      // instead of the skeleton while OrgContext resolves the user entity.
      //
      // Also treat "error + no data" as loading: when the dashboard
      // prefetch fails (e.g. quickfetch timeout on staging), the cache
      // entry lands in error state. React Query's isLoading (isPending &&
      // isFetching) is false for error state, so without this guard the
      // dashboard renders empty while the hook's own retry is in flight.
      // Showing the skeleton during that retry prevents the "connect your
      // calendar" flash. We don't gate on isFetching because there's a
      // brief tick between mount and retry start where isFetching is still
      // false — gating on it would flash empty for one frame.
      isLoading: !orgId
        ? false
        : meetings.isLoading ||
          (!!meetings.error && events.length === 0) ||
          (persisted ? false : !isIntegrationsLoaded),
      error: meetings.error?.message ?? null,
    }),
    [
      orgId,
      enabled,
      events,
      meetings.isLoading,
      meetings.error,
      todayCount,
      upcomingCount,
      integrationEntityIds,
      isIntegrationsLoaded,
      persisted,
      remindersEnabled,
      remindersList.results,
      remindersList.associatedObjects,
      canReadTasks,
      tasksList.results,
      tasksList.associatedObjects,
      taskPipelines,
      entityId,
    ]
  );
}

// ============================================================================
// Actions Hook (uses existing proposals API)
// ============================================================================

export function useDashboardActions(orgId: number | undefined, enabled = true) {
  // Progressive loading: fetch fast list first, then enhance with recipient data
  // This shows actions immediately while company badges load in background

  // Fast query: no recipient data (returns quickly)
  // Note: We omit includeRecipientData entirely (rather than setting it to false)
  // so the query key matches the default request and shares cache
  const {
    data: fastResponse,
    isLoading: fastLoading,
    isPlaceholderData: fastIsPlaceholder,
    isError: fastIsError,
    error: fastError,
  } = useMyPendingProposalsPaginated(
    orgId ?? 0,
    1, // page
    10, // pageSize - show top 10 on dashboard
    undefined, // no filter tag - get all categories
    {
      enabled: !!orgId && enabled,
    }
  );

  // Slow query: with recipient data (for company badges)
  // Only runs after fast query completes to avoid blocking initial render
  const {
    data: richResponse,
    isLoading: richLoading,
    isPlaceholderData: richIsPlaceholder,
  } = useMyPendingProposalsPaginated(
    orgId ?? 0,
    1, // page
    10, // pageSize - show top 10 on dashboard
    undefined, // no filter tag - get all categories
    {
      enabled: !!orgId && enabled && !fastLoading && !!fastResponse,
      includeRecipientData: true,
    }
  );

  // Use rich data when available, otherwise fall back to fast data.
  // Guard against stale placeholder data from keepPreviousData during org switches.
  // Note: We don't gate on richFetching because richResponse remains valid during
  // background refetches - we want to keep showing enriched data, not flash back
  // to non-enriched fast results.
  // Guard both responses against stale placeholder data during org switches.
  // keepPreviousData in useMyPendingProposalsPaginated means either response
  // can briefly hold data from the previous org while the new query loads.
  const safeFastResponse = fastIsPlaceholder ? undefined : fastResponse;
  const proposalsResponse =
    richResponse && !richIsPlaceholder ? richResponse : safeFastResponse;

  // Perf trace: first arrival of the fast list and of the recipient-enriched
  // list (the avatar upgrade), with row counts only.
  useEffect(() => {
    if (safeFastResponse) {
      bootMetrics.markOnce('data:proposals', {
        count: safeFastResponse.results?.length ?? 0,
      });
    }
  }, [safeFastResponse]);
  useEffect(() => {
    if (richResponse && !richIsPlaceholder) {
      bootMetrics.markOnce('data:proposals-rich');
    }
  }, [richResponse, richIsPlaceholder]);

  const queryClient = useQueryClient();
  // Track the orgId we last checked against so an org switch re-fires the
  // cache-check for the new org's key (the boot summary fires at +3s, so a
  // fast switch within that window must reflect the active org).
  const proposalsCheckedOrgIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!orgId) {
      return;
    }
    if (proposalsCheckedOrgIdRef.current === orgId) {
      return;
    }
    proposalsCheckedOrgIdRef.current = orgId;
    const proposalsKey = proposalQueryKeys.list(orgId, {
      resolution: 'pending',
      assigned_to_me: true,
      page: 1,
      page_size: 10,
    });
    bootMetrics.cacheCheck('proposals', queryClient, proposalsKey);
  }, [orgId, queryClient]);

  const counts = useMemo<ActionCategoryCounts>(() => {
    const filterTagCounts = proposalsResponse?.filter_tag_counts ?? {};
    return {
      onboarding: filterTagCounts.onboarding ?? 0,
      email: filterTagCounts.email ?? 0,
      pipeline_update: filterTagCounts.pipeline_update ?? 0,
      qualified_leads: filterTagCounts.crm_create ?? 0,
      total: proposalsResponse?.count ?? 0,
    };
  }, [proposalsResponse]);

  return useMemo(
    () => ({
      items: proposalsResponse?.results ?? [],
      counts,
      // Treat error+no-data as loading: when the prefetch fails (e.g.
      // quickfetch timeout), the cache is in error state and isLoading
      // (isPending && isFetching) is false — show skeleton while the
      // hook retries instead of flashing empty.
      isLoading:
        fastLoading || fastIsPlaceholder || (fastIsError && !fastResponse),
      isLoadingRecipientData: richLoading || richIsPlaceholder,
      error: fastError?.message ?? null,
    }),
    [
      proposalsResponse,
      counts,
      fastLoading,
      fastIsPlaceholder,
      fastIsError,
      fastResponse,
      richLoading,
      richIsPlaceholder,
      fastError,
    ]
  );
}

// ============================================================================
// Greeting Helper
// ============================================================================

export type TimePeriod =
  | 'morning'
  | 'afternoon'
  | 'evening'
  | 'night'
  | 'early';

export function getTimePeriod(): TimePeriod {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    return 'morning';
  }
  if (hour >= 12 && hour < 17) {
    return 'afternoon';
  }
  if (hour >= 17 && hour < 22) {
    return 'evening';
  }
  if (hour >= 2 && hour < 5) {
    return 'early';
  }
  return 'night';
}

/**
 * Get the timestamp of the next time-of-day boundary.
 * Used with `useRefreshAt` to update the greeting when the
 * period changes (morning->afternoon at noon, etc.).
 */
export function getNextTimePeriodBoundary(): number {
  const now = new Date();
  const hour = now.getHours();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // Use setHours (local-time) rather than fixed ms arithmetic so the
  // boundary lands on the correct wall-clock hour across DST transitions.
  // setHours normalizes out-of-range values (e.g. 29 -> 5am next day).
  const h = (hours: number) => {
    const boundary = new Date(today);
    boundary.setHours(hours, 0, 0, 0);
    return boundary.getTime();
  };

  if (hour < 2) {
    return h(2);
  }
  if (hour < 5) {
    return h(5);
  }
  if (hour < 12) {
    return h(12);
  }
  if (hour < 17) {
    return h(17);
  }
  if (hour < 22) {
    return h(22);
  }
  // After 10pm — next boundary is 02:00 tomorrow
  return h(24 + 2);
}

export function getGreeting(timePeriod: TimePeriod): string {
  const cacheKey = `dashboard_greeting_${timePeriod}`;
  let cached: string | null = null;

  try {
    cached = sessionStorage.getItem(cacheKey);
  } catch {
    // sessionStorage may not be available in private browsing or when disabled
  }

  if (!cached) {
    const pick = <T>(arr: T[]): T =>
      arr[Math.floor(Math.random() * arr.length)];

    switch (timePeriod) {
      case 'morning':
        cached = pick(['Good morning', 'Morning', 'Hey there']);
        break;
      case 'afternoon':
        cached = pick(['Good afternoon', 'Afternoon', 'Hey there']);
        break;
      case 'evening':
        cached = pick(['Good evening', 'Evening', 'Hey there']);
        break;
      case 'night':
        cached = pick(['Hey night owl', 'Hey there', 'Good evening']);
        break;
      case 'early':
        cached = pick(["You're up early", 'Early start', 'Hey there']);
        break;
    }
    try {
      sessionStorage.setItem(cacheKey, cached);
    } catch {
      // Ignore storage errors
    }
  }

  return cached;
}

// ============================================================================
// Dashboard Refresh Scheduling
// ============================================================================

/**
 * Compute the next timestamp when the dashboard header should refresh.
 * Returns the earliest of:
 * - The next time-period boundary (greeting change)
 * - The next local midnight (date-scoped counts roll over)
 * - The next upcoming meeting start (subtitle count update)
 *
 * Midnight is included explicitly because no greeting boundary lands on
 * midnight (the night period now spans 22:00–02:00): without it, date-scoped
 * content would not refresh exactly at the day rollover.
 */
function getNextDashboardRefreshTime(events: CalendarEvent[]): number {
  const nextBoundary = getNextTimePeriodBoundary();
  const nextMidnight = getNextLocalMidnight();

  const now = Date.now();
  let nextMeetingStart: number | null = null;
  for (const event of events) {
    const startMs = new Date(event.start_time).getTime();
    if (startMs > now) {
      if (nextMeetingStart === null || startMs < nextMeetingStart) {
        nextMeetingStart = startMs;
      }
    }
  }

  const baseRefresh = Math.min(nextBoundary, nextMidnight);
  if (nextMeetingStart === null) {
    return baseRefresh;
  }
  return Math.min(baseRefresh, nextMeetingStart);
}

// ============================================================================
// Main Dashboard Data Hook
// ============================================================================

export function useDashboardData(
  orgId: number | undefined,
  user: { display?: string; username?: string } | null,
  options: { actionsEnabled?: boolean } = {}
): DashboardData {
  const { actionsEnabled = true } = options;

  // Calendar data
  const calendar = useCalendarEvents(orgId);

  // Actions data — gated by actionsEnabled (viewer-seat users without ai_actions
  // feature would get a 403 from the proposals endpoint)
  const actions = useDashboardActions(orgId, actionsEnabled);

  // Dashboard metrics — below the fold. Held back until the fold (meetings +
  // actions) has its data so the three aggregate requests don't compete with
  // the first paint; their section shows a skeleton until they arrive.
  const { data: metricsData, isLoading: metricsLoading } = useDashboardMetrics(
    orgId,
    { enabled: !calendar.isLoading && !actions.isLoading }
  );

  // Greeting (memoized for session)
  const timePeriod = getTimePeriod();
  const greeting = useMemo(() => getGreeting(timePeriod), [timePeriod]);

  // Schedule a refresh at the next greeting boundary or meeting start,
  // whichever comes first. This keeps the greeting text, subtitle counts,
  // and "X more meetings" indicator current without a page reload.
  useRefreshAt(getNextDashboardRefreshTime(calendar.events));

  // First name extraction
  const firstName = useMemo(() => {
    const display = user?.display;
    if (display) {
      const first = display.split(' ')[0];
      if (first) {
        return first;
      }
    }
    return user?.username || LOADING_FALLBACK_NAME;
  }, [user?.display, user?.username]);

  // Build metrics with calendar-derived data
  const metrics: DashboardMetricsData = useMemo(
    () => ({
      totalPipelineValue: metricsData?.totalPipelineValue ?? 0,
      weightedPipelineValue: metricsData?.weightedPipelineValue ?? 0,
      dealsCount: metricsData?.dealsCount ?? 0,
      activeDealCount: metricsData?.activeDealCount ?? 0,
      contactsCount: metricsData?.contactsCount ?? 0,
      companiesCount: metricsData?.companiesCount ?? 0,
      meetingsToday: calendar.todayCount,
      meetingsUpcoming: calendar.upcomingCount,
      aiActionsPrepared: actions.counts.total,
      currencyCode: metricsData?.currencyCode ?? 'USD',
      pipelineStages: metricsData?.pipelineStages ?? [],
      isLoading: metricsLoading,
    }),
    [
      metricsData,
      metricsLoading,
      calendar.todayCount,
      calendar.upcomingCount,
      actions.counts.total,
    ]
  );

  return {
    calendar,
    actions,
    metrics,
    greeting,
    firstName,
  };
}

// Re-export utilities
export { formatCurrency };
